/**
 * Compute how many digits the frame component needs for a given fps.
 * Uses Math.ceil so fractional rates like 99.7 → 100 → 3 digits.
 * Always at least 2 digits.
 */
export function frameDigitCount(fps: number): number {
  return Math.max(2, String(Math.ceil(fps)).length);
}

/**
 * Normalize an NTSC-ish fractional fps to its exact rational value.
 *
 * Callers commonly pass truncated decimals like 29.97 or 23.976. Used as
 * a rate (seconds × fps → frame), these truncated values drift from the
 * true 30000/1001 / 24000/1001 / 60000/1001 by ~3 × 10⁻⁵ frame per
 * second. Over a few minutes that's enough to flip Math.floor's output
 * by ±1 frame at frame boundaries — which manifests as the timecode
 * display and the visible video frame disagreeing.
 *
 * Whole-number fps values pass through unchanged.
 */
export function normalizeFps(fps: number): number {
  if (Number.isInteger(fps)) return fps;
  const ceil = Math.ceil(fps);
  // Only the standard NTSC family — don't second-guess unusual rates.
  if (ceil === 24 && fps > 23 && fps < 24) return 24000 / 1001;
  if (ceil === 30 && fps > 29 && fps < 30) return 30000 / 1001;
  if (ceil === 60 && fps > 59 && fps < 60) return 60000 / 1001;
  return fps;
}

/**
 * Convert a wall-clock time (seconds) to a frame index, using the
 * convention that frame N spans [N/fps, (N+1)/fps). This MUST match
 * the convention used everywhere else (useFrameAccurateTime,
 * incrementFrames) — if formatters use round() and detectors use
 * floor(), the displayed timecode can advance while the actual decoded
 * frame hasn't, or vice versa.
 *
 * Seeks computed by incrementFrames intentionally land mid-frame
 * ((N + 0.5)/fps), so the floor-based mapping is robust against the
 * IEEE-754 drift that originally pushed this function to Math.round.
 */
function timeToFrameIndex(seconds: number, fps: number): number {
  return Math.floor(seconds * normalizeFps(fps));
}

export function formatTimecode(
  seconds: number,
  fps: number = 24,
  startFrame: number = 0,
  dropFrame: boolean = false,
): string {
  const frameDigs = frameDigitCount(fps);
  if (!isFinite(seconds) || seconds < 0) return `00:00:00:${'0'.repeat(frameDigs)}`;

  const totalFrames = timeToFrameIndex(seconds, fps) + startFrame;

  if (dropFrame && Math.ceil(fps) !== Math.floor(fps) && (Math.ceil(fps) === 30 || Math.ceil(fps) === 60)) {
    return formatDropFrame(totalFrames, fps, frameDigs);
  }

  const roundFps = Math.round(fps);
  const ff = totalFrames % roundFps;
  const totalSecs = Math.floor(totalFrames / roundFps);
  const ss = totalSecs % 60;
  const mm = Math.floor(totalSecs / 60) % 60;
  const hh = Math.floor(totalSecs / 3600);

  return `${pad(hh)}:${pad(mm)}:${pad(ss)}:${padFrames(ff, frameDigs)}`;
}

/**
 * Convert an actual frame count to a drop-frame timecode string.
 *
 * Drop-frame timecode skips frame numbers :00 and :01 at every minute
 * boundary EXCEPT every 10th minute, keeping wall-clock time aligned
 * with the 29.97/59.94 fps rate.
 *
 * The algorithm:
 * 1. Divide frame count into 10-minute blocks (17982 actual frames each for 29.97)
 * 2. Within each block, count how many minute boundaries have been crossed
 * 3. Each non-10th-minute boundary adds `dropFrames` (2 for 29.97) to the display count
 * 4. Convert the adjusted display frame count to HH:MM:SS;FF
 */
function formatDropFrame(totalFrames: number, fps: number, frameDigs: number): string {
  // Use the exact NTSC rational here so `fps * 60` and `fps * 600`
  // produce their true values (1798.2, 17982) regardless of whether
  // the caller passed 29.97 or 30000/1001.
  const trueFps = normalizeFps(fps);
  const roundFps = Math.round(trueFps); // 30 or 60
  const dropFrames = roundFps === 30 ? 2 : 4;

  // Nominal frames per minute (30 * 60 = 1800), NOT the true-rate count.
  // SMPTE drop-frame counts against the nominal rate and drops display
  // numbers to stay aligned, so the within-block divisor must be
  // `nominalFramesPerMin - dropFrames` (1798 for 29.97, 3596 for 59.94).
  const nominalFramesPerMin = roundFps * 60;
  // Actual frames per 10-minute block (e.g. 29.97 * 600 = 17982)
  const framesPer10Min = Math.round(trueFps * 600);

  const d = Math.floor(totalFrames / framesPer10Min);
  const m = totalFrames % framesPer10Min;

  // Total drops: 9 drops per 10-min block (minutes 1-9) × dropFrames each,
  // plus within the current block, one drop per minute boundary crossed.
  let adjustedFrames: number;
  if (m < dropFrames) {
    // First `dropFrames` frames of a 10-min block — no within-block drops
    adjustedFrames = totalFrames + dropFrames * 9 * d;
  } else {
    adjustedFrames = totalFrames
      + dropFrames * 9 * d
      + dropFrames * Math.floor((m - dropFrames) / (nominalFramesPerMin - dropFrames));
  }

  const ff = adjustedFrames % roundFps;
  const totalSecs = Math.floor(adjustedFrames / roundFps);
  const ss = totalSecs % 60;
  const mm = Math.floor(totalSecs / 60) % 60;
  const hh = Math.floor(totalSecs / 3600);

  return `${pad(hh)}:${pad(mm)}:${pad(ss)};${padFrames(ff, frameDigs)}`;
}

function pad(num: number, length: number = 2): string {
  let outStr = "";

  // Get string length
  const curLength = stringLengthForNumber(num);

  for (let i = 0; i < length - curLength; i++) {
    outStr += "0";
  }

  outStr += num;

  return outStr;
}

function padFrames(n: number, digits: number): string {
  return String(n).padStart(digits, '0');
}

export function formatTimeOnly(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '00:00:00';

  const totalSecs = Math.floor(seconds);
  const ss = totalSecs % 60;
  const mm = Math.floor(totalSecs / 60) % 60;
  const hh = Math.floor(totalSecs / 3600);

  return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
}

/**
 * Convert a timecode string (HH:MM:SS:FF or HH:MM:SS;FF) back to seconds.
 */
export function timecodeToSeconds(
  timecode: string,
  fps: number,
  startFrame: number = 0,
  dropFrame: boolean = false,
): number {
  const frames = timecodeToFrameCount(timecode, fps, dropFrame);
  // Use the exact rate so frame N → wall-clock time matches the
  // frame-to-time mapping used elsewhere. If you intend to feed this
  // value to a video element seek, see frameToSeekSeconds below — it
  // adds the half-frame offset that makes browser frame stepping
  // reliable.
  return Math.max(0, (frames - startFrame) / normalizeFps(fps));
}

/**
 * Convert a frame index to a wall-clock time suitable for assigning to
 * HTMLMediaElement.currentTime when you want frame N to be the visible
 * frame after the seek.
 *
 * Browsers seek to the nearest frame whose PTS ≤ requested time. If we
 * ask for exactly N/fps, IEEE-754 multiplication can land us at
 * N/fps − ε, and the decoder picks frame N − 1. By aiming for the
 * middle of the target frame ((N + 0.5)/fps) we sit safely inside
 * [N/fps, (N+1)/fps), so the decoder reliably picks frame N.
 *
 * This is the function you want for any seek that's computed from a
 * frame number; plain `frame / fps` is fine for arithmetic but not for
 * assigning to currentTime.
 */
export function frameToSeekSeconds(frame: number, fps: number): number {
  return (frame + 0.5) / normalizeFps(fps);
}

/**
 * Convert a timecode string to an actual (absolute) frame count.
 * For drop-frame timecodes, this accounts for the skipped display
 * frame numbers and returns the real frame number.
 */
export function timecodeToFrameCount(
  timecode: string,
  fps: number,
  dropFrame: boolean = false,
): number {
  const parts = timecode.split(/[:;]/);
  if (parts.length < 4) return 0;

  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  const s = parseInt(parts[2], 10) || 0;
  const f = parseInt(parts[3], 10) || 0;

  const trueFps = normalizeFps(fps);
  const roundFps = Math.round(trueFps);
  let totalFrames: number;

  if (dropFrame && Math.ceil(trueFps) !== Math.floor(trueFps) && (Math.ceil(trueFps) === 30 || Math.ceil(trueFps) === 60)) {
    const dropCount = Math.ceil(trueFps) === 30 ? 2 : 4;
    // Drop-frame correction uses total minutes (hours × 60 + minutes),
    // not just `m`; using `m` alone gives wrong results whenever hours > 0.
    const totalMinutes = h * 60 + m;
    totalFrames = (h * 3600 + m * 60 + s) * roundFps + f;
    totalFrames -= dropCount * (totalMinutes - Math.floor(totalMinutes / 10));
  } else {
    totalFrames = (h * 3600 + m * 60 + s) * roundFps + f;
  }

  return totalFrames;
}

export function ariaTimeText(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0 seconds';

  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  const parts: string[] = [];
  if (h > 0) parts.push(`${h} hour${h !== 1 ? 's' : ''}`);
  if (m > 0) parts.push(`${m} minute${m !== 1 ? 's' : ''}`);
  parts.push(`${s} second${s !== 1 ? 's' : ''}`);

  return parts.join(' ');
}

export function formatUserTimecode(userString: string, dropFrame: boolean, startTimecode: string) : string {
  if (!userString) return startTimecode;

  // Strip unsupported characters
  userString = userString.replace(/[^0-9:;,.]/g, "");

  // Turn . or , into 00, but only if they're at the end
  if (userString.endsWith("....")) userString = "00:00:00:00";
  if (userString.endsWith("...")) userString = userString.substring(0, userString.length - 3) + ":00:00:00";
  if (userString.endsWith("..")) userString = userString.substring(0, userString.length - 2) + ":00:00";
  if (userString.endsWith(".")) userString = userString.substring(0, userString.length - 1) + ":00";

  // Tokenize
  let tokens = userString.split(/([:;,.])/g);

  if (tokens.length === 0) return startTimecode;

  // Split up numbers longer than 2 characters if the user entered something like 01231412
  let tempString = "";
  let shouldReSplit = false;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    if (token.length > 2 && Number.isInteger(parseInt(token))) {
      // Insert colons
      const colonCount = Math.ceil(token.length / 2);

      // Start backwards
      let tempString2 = "";
      for (let j = 0; j < colonCount; j++) {
        let subLength = 2;
        let startPos = token.length - ((j + 1) * subLength);
        if (startPos < 0) {
          startPos = 0;
          subLength = 1;
        }

        tempString2 = token.substr(startPos, subLength) + (tempString2.length >= 2 ? ":":"") + tempString2;
      }

      shouldReSplit = true;

      if (tempString.length > 0 && tempString.substr(tempString.length - 1) !== ":") tempString += ":";
      tempString += tempString2;

    } else {
      tempString += token;
    }
  }

  // Split again if needed
  if (shouldReSplit) {
    tokens = tempString.split(/([\:\;\,\.])/g);
  }

  // Truncate if too long
  if (tokens.length > 7) tokens.length = 7;

  // Prepend start timecode if too short
  if (tokens.length < 7) {
    // Tokenize start timecode
    const startTokens = startTimecode.split(/([\:\;\,\.])/g);

    tokens = startTokens.slice(0, 7 - tokens.length).concat(tokens);
  }

  // Make sure numbers are two digits
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    if (token.length === 1 && Number.isInteger(parseInt(token))) {
      tokens[i] = "0" + token;
    }
  }

  // Set drop-frame correctly
  if (tokens.length < 7) {
    console.log("Unable to parse timecode");
    return startTimecode;
  }

  tokens[5] = (dropFrame ? ";":":");

  // Create string
  const timecode = tokens.join("");

  if (validateTimecode(timecode)) {
    return timecode;
  } else {
    console.log("Failed to validate timecode " + timecode);
    return startTimecode;
  }
}

export function validateTimecode(timecode: string) : boolean {
  // Separator before frames may be ':' (non-drop) or ';' (drop). Frames are
  // 2-3 digits to allow high frame rates (fps >= 100), matching frameDigitCount.
  return timecode.match(/^([0-9][0-9]):([0-5][0-9]):([0-5][0-9])[:;]([0-9]{2,3})$/) !== null;
}

export function timecodeFromFrameCount(frameCount: number, fps: number, dropFrame: boolean) : string {
  // Sanity checks
  if (fps <= 0) fps = 30.0;

  var totalFrames = frameCount;

  let frames = 0, seconds = 0, minutes = 0, hours = 0;

  const roundFps = Math.round(normalizeFps(fps));

  // Account for drop frame timecode
  if (dropFrame) {
    // Drop frames per minute boundary: 2 at 29.97, 4 at 59.94.
    const dropFrames = roundFps === 60 ? 4 : 2;
    // Remove `dropFrames` from each minute, except for the 10th minutes
    // (e.g. 18 frames from each 10 minutes at 29.97). Count minutes against
    // the nominal rate so the boundaries line up with formatDropFrame.
    const minCount = Math.floor(totalFrames / (roundFps * 60)); // How many minutes?
    const tenMinCount = Math.floor(minCount / 10.0); // How many 10 minute segments?

    totalFrames += dropFrames * (minCount - tenMinCount);
  }

  let totalSeconds = Math.floor(totalFrames / Math.ceil(fps));

  frames = Math.round(modulus(totalFrames, Math.ceil(fps)));
  seconds = Math.floor(totalSeconds % 60.0);
  minutes = Math.floor((totalSeconds / 60.0) % 60.0);
  hours = Math.floor(((totalSeconds / 60.0) / 60.0) % 24.0);

  var fpsLength = stringLengthForNumber(Math.ceil(fps));

  if (dropFrame) {
    return pad(hours) + ':' + pad(minutes) + ':' + pad(seconds) + ';' + pad(frames, (fpsLength < 2 ? 2 : fpsLength));
  } else {
    return pad(hours) + ':' + pad(minutes) + ':' + pad(seconds) + ':' + pad(frames, (fpsLength < 2 ? 2 : fpsLength));
  }
}

function stringLengthForNumber(num: number) : number {
  return num.toString().length;
}

function modulus(a: number, b: number) : number {
  const result = Math.floor( a / b );
  return a - ( result ) * b;
}